import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Field, touch } from 'redux-form';
import { Selectors } from '../../store/selectors';
import ReduxFormInputField from './ReduxFormInputField';
import CheckBoxUI from './CheckBoxUI';
import { Checkbox } from 'redux-form-material-ui';
import FooterUI from './FooterUI';
import { optimizelyUtils } from '@makemydeal/dr-browser-utils';
import { ILeadFormConstants } from './LeadFormConstants';
import { CardHeaderUI } from '@makemydeal/dr-activities-common';

enum Provider {
    'facebook',
    'google'
}

class ReduxLeadForm extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            isSocialClicked: false
        };
    }

    componentDidMount() {
        this.props.uspsZipInitialize();
    }

    touchFields(field: string) {
        // TODO: accepts array of strings
        this.props.dispatch(touch('leadForm', `shopper.${field}`));
        this.props.onCommunicationPreferenceChange();
    }

    labelIconClick(refsName: string): void {
        const element = ReactDOM.findDOMNode(this.refs[refsName]) as HTMLInputElement;
        element.focus();
    }

    getMessageText(message: string): string {
        const { maxMessageLength } = this.props;
        // if we have a message and its length is one less than the max use 'character'
        const messageText = message && message.length === maxMessageLength - 1 ? 'character' : 'characters';

        return `${messageText} remaining`;
    }

    renderHeadingSection = (leadFormConstants: ILeadFormConstants) => {
        if (!leadFormConstants.heading) return null;
        return (
            <>
                <div className="summary-lead-form-information">{leadFormConstants.heading}</div>
                <div className="summary-lead-form-disclaimer">{leadFormConstants.subHeading}</div>
            </>
        );
    };

    renderHeader = () => {
        const { isLeadFormLite, hideTitle } = this.props;
        if (!hideTitle) {
            if (isLeadFormLite) {
                return (
                    <p className="lead-form-information" id="sp_lead_form_info">
                        {this.props.showSocialOAuth
                            ? 'Use your social account to quickly submit your information'
                            : "What's the best way to reach you?"}
                    </p>
                );
            } else {
                return (
                    <div>
                        <h3 className="lead-form-title" id="sp_lead_form_title">
                            Send My Deal Summary
                        </h3>
                        <p className="lead-form-information" id="sp_lead_form_info">
                            Save time on your purchase
                        </p>
                        <p className="dr-sp-disclaimer lead-form-disclaimer" id="sp_lead_form_disclaimer">
                            Send your progress and receive a copy of your deal by email.
                        </p>
                    </div>
                );
            }
        }
        return null;
    };

    renderMessageBox = () => {
        const { hideAdditionalInfo, onMessageBlur, messageNormalizer, maxMessageLength, messageValue } = this.props;
        const messageLength = messageValue ? maxMessageLength - messageValue.length : maxMessageLength;
        if (hideAdditionalInfo) {
            return null;
        }

        return (
            <div className="form-group">
                <p className="message-question" id="sp_lead_form_msg_question">
                    Do you have any additional information?
                </p>
                <Field
                    id="sp_shopper_message"
                    className="form-control"
                    name="message"
                    component="textarea"
                    placeholder="Ex: I'd like to buy a car in the next week. (Optional)"
                    data-analytics="input-deal-message"
                    onBlur={(event) => onMessageBlur(event)}
                    normalize={messageNormalizer}
                    rows="2"
                />
                <div id="sp_lead_form_msg_counter" className="lead-msg-count">
                    {messageLength} {this.getMessageText(messageValue)}
                </div>
            </div>
        );
    };

    onContinueToNextStepClick = () => {
        const { onContinueToNextStepClick } = this.props;
        onContinueToNextStepClick();
    };

    renderButtonFooter = () => {
        const {
            offerType,
            offerSubmitting,
            offerHasBeenSent,
            hideContinueButton,
            continueToNextStepPostOfferEnabled,
            hideNextStepsLink,
            zipCodeLoading
        } = this.props;

        if (hideContinueButton) {
            return null;
        }
        if (this.props.isLeadFormLite) {
            return (
                <FooterUI
                    continueToNextStepPostOfferEnabled={continueToNextStepPostOfferEnabled}
                    offerHasBeenSent={offerHasBeenSent}
                    onActionButtonClick={() => null}
                    onContinueLinkClick={hideNextStepsLink ? undefined : this.onContinueToNextStepClick}
                    processing={offerSubmitting || zipCodeLoading}
                    offerType={offerType}
                />
            );
        } else {
            return (
                <FooterUI
                    continueToNextStepPostOfferEnabled={this.props.continueToNextStepPostOfferEnabled}
                    offerHasBeenSent={this.props.offerHasBeenSent}
                    offerType={offerType}
                    onActionButtonClick={() => null}
                    processing={this.props.offerSubmitting || zipCodeLoading}
                />
            );
        }
    };

    checkForNonEmptyValue = (e: any): boolean => {
        const { initialValues, leadFormFieldState } = this.props;
        const formFieldName = e.target.name.split('.')[1];
        const validNonEmptyValueWasEntered = e.target.value !== '' && e.target.value !== initialValues.shopper[formFieldName];
        if (validNonEmptyValueWasEntered) {
            leadFormFieldState[formFieldName].hasBeenUpdated = true;
            leadFormFieldState[formFieldName].previousValue = e.target.value;
            return true;
        } else {
            return false;
        }
    };

    onHandleOAuth = (provider: Provider) => {
        if (!this.state.isSocialClicked) {
            if (provider === 0) {
                optimizelyUtils.track('facebookClick');
            } else {
                optimizelyUtils.track('googleClick');
            }

            this.setState({ isSocialClicked: true }, () => this.scrollToMessage());
        }
    };

    scrollToMessage = () => {
        const element = document.getElementById('sp_shopper_firstname');
        const socialOAuthMsg = document.getElementById('socialOAuthMsg') as any;
        const frame = document.getElementById('dr-shopper-app');
        const scrollToYCoord = socialOAuthMsg.offsetTop - 15;
        const body = document.querySelector('body') as any;
        const isIE = body.classList.contains('browser-ie');
        const isIEEdge = body.classList.contains('browser-ie-edge');

        if (element) element.focus();

        if (frame) {
            if (isIE || isIEEdge) {
                frame.scrollTop = scrollToYCoord;
            } else {
                frame.scrollTo({
                    top: scrollToYCoord,
                    behavior: 'smooth'
                });
            }
        }
    };

    renderSocialOAuthTest = () => {
        const { showSocialOAuth } = this.props;
        if (!showSocialOAuth) {
            return null;
        }

        const fbookBtn = (
            <div
                className="socialOAuth--fbook"
                onClick={(event) => {
                    event.preventDefault();
                    this.onHandleOAuth(Provider.facebook);
                }}
            >
                <button className="socialOAuth--btn fbook-btn">
                    <figure className="svg-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 216 216" color="#FFFFFF">
                            <path
                                fill="#ffffff"
                                d="
                                    M204.1 0H11.9C5.3 0 0 5.3 0 11.9v192.2c0 6.6 5.3 11.9 11.9
                                    11.9h103.5v-83.6H87.2V99.8h28.1v-24c0-27.9 17-43.1 41.9-43.1
                                    11.9 0 22.2.9 25.2 1.3v29.2h-17.3c-13.5 0-16.2 6.4-16.2
                                    15.9v20.8h32.3l-4.2 32.6h-28V216h55c6.6 0 11.9-5.3
                                    11.9-11.9V11.9C216 5.3 210.7 0 204.1 0z"
                            ></path>
                        </svg>
                    </figure>
                    <span>Continue with Facebook</span>
                </button>
            </div>
        );

        const googleBtn = (
            <div
                className="socialOAuth--google"
                onClick={(event) => {
                    event.preventDefault();
                    this.onHandleOAuth(Provider.google);
                }}
            >
                <button className="socialOAuth--btn google-btn">
                    <figure className="svg-icon">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="18px" height="18px" viewBox="0 0 48 48">
                            <g>
                                <path
                                    fill="#EA4335"
                                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9
                                    2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98
                                    6.19C12.43 13.72 17.74 9.5 24 9.5z"
                                ></path>
                                <path
                                    fill="#4285F4"
                                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58
                                    2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                                ></path>
                                <path
                                    fill="#FBBC05"
                                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92
                                    16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                                ></path>
                                <path
                                    fill="#34A853"
                                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15
                                    1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98
                                    6.19C6.51 42.62 14.62 48 24 48z"
                                ></path>
                            </g>
                        </svg>
                    </figure>
                    <span>Continue with Google</span>
                </button>
            </div>
        );

        const socialTestUI = (
            <div className="socialOAuth--wrapper">
                <div className={this.state.isSocialClicked ? 'socialOAuth hasError' : 'socialOAuth'}>
                    {' '}
                    {fbookBtn} {googleBtn}{' '}
                </div>
                {this.state.isSocialClicked ? (
                    <div id="socialOAuthMsg" className="socialOAuth--errMsg">
                        Thank you for your interest. This feature is in development.
                    </div>
                ) : null}
                <hr className="socialOAuth--break" data-content="OR"></hr>
            </div>
        );
        return socialTestUI;
    };

    render() {
        const {
            error,
            constructErrorMessage,
            handleSubmit,
            onEmailBlur,
            onFirstNameBlur,
            onLastNameBlur,
            onPhoneBlur,
            offerSubmitFailed,
            submitFailed,
            shopperConsentText,
            currentShopperInfo,
            isTcpaEnabled,
            isTCPACheckboxHidden,
            CCPAConsentText,
            hidePhoneNumber,
            hideContactPreffered,
            hideUserConsent,
            leadFormCCPAShown,
            hideMessageBox,
            isLeadFormShort,
            hideEmailPhoneFields,
            hideNameFields,
            leadFormConstants,
            propCopySubHeader,
            isSingleColumn,
            isLeadFormSingleCol,
            isPhoneNumberRequired
        } = this.props;

        const { normalizePhone } = Selectors.getInstance().additionalSelectors.validateSelectors;
        const shopperInfo = currentShopperInfo && currentShopperInfo.contactPreferences;
        const hideContactPreferencesError = shopperInfo ? shopperInfo.email || shopperInfo.phone || shopperInfo.sms : false;
        const errorMessage = constructErrorMessage(error, submitFailed, offerSubmitFailed);
        const errorMsgComponent = (
            <div className={isLeadFormShort ? 'form-group' : 'form-group text-right'}>
                <span className="message-error"> {errorMessage} </span>
            </div>
        );
        const errorClsContactsPreference = 'preferred-method-communication'.concat(
            !hideContactPreferencesError ? ' error-ring' : ''
        );
        const leadformLayout = (fullWidthClass: string, containerClass: string) => {
            let leadFormClass;
            if (isLeadFormSingleCol) {
                leadFormClass = 'form-group col';
            } else if (isSingleColumn) {
                leadFormClass = fullWidthClass;
            } else {
                leadFormClass = containerClass;
            }
            return leadFormClass;
        };

        const showOptionalLabel = (isPhoneNumberRequired || shopperInfo?.phone || shopperInfo?.sms) ? '' : '(Optional)'
        return (
            <div
                style={{ width: '100%' }}
                className={isLeadFormShort ? 'dr-short-lead dr-leadform-wrapper' : 'dr-leadform-wrapper'}
            >
                <form onSubmit={handleSubmit} className="lead-form">
                    <CardHeaderUI
                        cardHeading={leadFormConstants.pageTitle}
                        secondaryText={propCopySubHeader ? propCopySubHeader : null}
                    />
                    {this.renderHeader()}
                    {this.renderSocialOAuthTest()}
                    {hideNameFields ? null : (
                        <>
                            <div className={leadformLayout('full-width-name-container', 'full-width-name-container')}>
                                <div className="form-group">
                                    <Field
                                        id="sp_shopper_firstname"
                                        ref="spLfFirstName"
                                        withRef={true}
                                        name="shopper.firstName"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-first-name"
                                        type="text"
                                        onBlur={(e) => this.checkForNonEmptyValue(e) && onFirstNameBlur(e)}
                                        label="First Name"
                                    />
                                </div>
                            </div>
                            <div className={leadformLayout('full-width-name-container', 'full-width-name-container')}>
                                <div className="form-group">
                                    <Field
                                        id="sp_shopper_lastname"
                                        ref="spLfLastName"
                                        withRef={true}
                                        name="shopper.lastName"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-last-name"
                                        type="text"
                                        onBlur={(event) => this.checkForNonEmptyValue(event) && onLastNameBlur(event)}
                                        label="Last Name"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {hideEmailPhoneFields ? null : (
                        <div className={leadformLayout('full-width-email-phone-container ', 'email-phone-container')}>
                            <div className="form-group col">
                                <Field
                                    id="sp_shopper_email"
                                    ref="spLfEmail"
                                    withRef={true}
                                    name="shopper.email"
                                    component={ReduxFormInputField}
                                    analyticsId="input-shopper-email"
                                    type="text"
                                    onBlur={(event) => this.checkForNonEmptyValue(event) && onEmailBlur(event)}
                                    label="Email"
                                />
                            </div>

                            {hidePhoneNumber ? null : (
                                <div className="form-group col">
                                    <Field
                                        id="sp_shopper_phone"
                                        ref="spLfPhone"
                                        withRef={true}
                                        name="shopper.phone"
                                        component={ReduxFormInputField}
                                        analyticsId="input-shopper-phone"
                                        normalize={normalizePhone}
                                        type="tel"
                                        onBlur={(event) => this.checkForNonEmptyValue(event) && onPhoneBlur(event)}
                                        label={`Phone${showOptionalLabel}`}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {hideContactPreffered ? null : (
                        <div className="communication-preference-container">
                            <h5 className="title">Please select your preferred method of contact.</h5>
                            <p className="information">You may still receive correspondence in other ways.</p>
                            <div className={errorClsContactsPreference}>
                                <Field
                                    id="sp_shopper_email_preference"
                                    name="shopper.contactPreferences.email"
                                    component={Checkbox}
                                    label="Email"
                                    onCheck={() => this.touchFields('email')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.email ? 'bold' : 'normal' }}
                                />
                                <Field
                                    id="sp_shopper_call_preference"
                                    name="shopper.contactPreferences.phone"
                                    component={Checkbox}
                                    label="Call"
                                    onCheck={() => this.touchFields('phone')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.phone ? 'bold' : 'normal' }}
                                />
                                <Field
                                    id="sp_shopper_text_preference"
                                    name="shopper.contactPreferences.sms"
                                    component={Checkbox}
                                    label="Text"
                                    onCheck={() => this.touchFields('phone')}
                                    iconStyle={{ stroke: '#fff' }}
                                    labelStyle={{ fontWeight: shopperInfo && shopperInfo.sms ? 'bold' : 'normal' }}
                                />
                            </div>
                        </div>
                    )}

                    {hideMessageBox ? null : this.renderMessageBox()}
                    {!hideUserConsent && isTcpaEnabled && (
                        <div className="form-check user-consent-group">
                            <Field
                                id="sp_shopper_consent"
                                name="shopper.userConsent"
                                component={CheckBoxUI}
                                normalize={(value) => (value ? 'checked' : 'unchecked')}
                                labelText={shopperConsentText}
                                isHidden={isTCPACheckboxHidden}
                            ></Field>
                        </div>
                    )}
                    {errorMessage ? errorMsgComponent : null}
                    {leadFormCCPAShown && <div data-testid="shopper.ccpaConsent">{CCPAConsentText}</div>}
                    <div className="leadform__footer">{this.renderButtonFooter()}</div>
                </form>
            </div>
        );
    }
}

export default ReduxLeadForm;
